import {
  Collapsible,
  CollapsibleContent,
  CollapsibleTrigger,
} from '@heidi/design-system/collapsible';
import { focusRing } from '@heidi/design-system/styles';
import {
  TypographyCaptionBold,
  typographyVariant,
} from '@heidi/design-system/typography';
import { cn } from '@heidi/utils';
import { ChevronDown } from 'lucide-react';
import { FormattedMessage } from 'react-intl';

import { workflowsSidebarSectionMessages as m } from '../../../data/workflows/components/WorkflowsSidebarSection.messages';
import {
  AgentHomeRoutinesList,
  ChatRoutinesList,
  WorkflowsSidebarErrorState,
  WorkflowsSidebarLoadingState,
  type WorkflowsSidebarRoutineListsProps,
} from './WorkflowsSidebarRoutineLists';

type WorkflowsSidebarSectionShellProps = {
  isAgentHomePresentation: boolean;
  sectionOpen: boolean;
  onSectionOpenChange: (open: boolean) => void;
};

/**
 * The section's content, discriminated by `status`: the two happy-path lists
 * (`list`, same props as before) plus the two states that are not a happy
 * list of rows — loading (both `useWorkflows` and `useWorkflowSchedules` must
 * land before this ever reads `list`) and error (either query failed).
 */
export type WorkflowsSidebarCollapsibleSectionProps = WorkflowsSidebarSectionShellProps &
  (
    | ({ status: 'list' } & WorkflowsSidebarRoutineListsProps)
    | { status: 'loading' }
    | { status: 'error' }
  );

export function WorkflowsSidebarCollapsibleSection(
  props: WorkflowsSidebarCollapsibleSectionProps
): JSX.Element {
  const { isAgentHomePresentation, sectionOpen, onSectionOpenChange } = props;

  const content =
    props.status === 'loading' ? (
      <WorkflowsSidebarLoadingState />
    ) : props.status === 'error' ? (
      <WorkflowsSidebarErrorState />
    ) : isAgentHomePresentation ? (
      <AgentHomeRoutinesList {...props} />
    ) : (
      <ChatRoutinesList {...props} />
    );

  return (
    <Collapsible open={sectionOpen} onOpenChange={onSectionOpenChange} className="w-full">
      {isAgentHomePresentation ? (
        // Same row as the Recents date-group headers: the whole h-8 px-2 row
        // toggles, and its chevron appears on hover / focus-visible (always
        // shown where the pointer cannot hover).
        <CollapsibleTrigger asChild>
          <button
            type="button"
            data-testid="sidebar-routines-section-toggle"
            className={cn(
              'group/routines-heading flex h-8 w-full items-center gap-1 overflow-hidden whitespace-nowrap rounded-md px-2',
              'text-text-secondary',
              focusRing,
              'transition-colors duration-200 ease-out motion-reduce:transition-none',
              'hover:text-text-primary'
            )}
          >
            <span
              className={cn(
                typographyVariant({ variant: 'p2' }),
                'min-w-0 flex-1 truncate text-start'
              )}
              data-testid="sidebar-routines-heading"
            >
              <FormattedMessage {...m.scheduledRoutines} />
            </span>
            {/* size-7 slot matches the Recents header's 28px icon buttons so both share one right edge. */}
            <span
              aria-hidden="true"
              className="flex size-7 shrink-0 items-center justify-center"
            >
              <ChevronDown
                data-testid="sidebar-routines-section-chevron"
                className={cn(
                  'size-4 text-text-secondary pointer-fine:sm:opacity-0',
                  'transition-[opacity,transform] duration-200 ease-out motion-reduce:transition-none',
                  'group-hover/routines-heading:opacity-100 group-focus-visible/routines-heading:opacity-100',
                  !sectionOpen && '-rotate-90 rtl:rotate-90'
                )}
              />
            </span>
          </button>
        </CollapsibleTrigger>
      ) : (
        <CollapsibleTrigger
          className={cn(
            'group/routines flex w-full items-center justify-between p-2 pb-1'
          )}
        >
          <TypographyCaptionBold className="text-text-tertiary">
            <FormattedMessage {...m.scheduledRoutines} />
          </TypographyCaptionBold>
          <ChevronDown
            size={14}
            className={cn(
              'text-text-tertiary opacity-0 transition-[transform,opacity] duration-200 ease-out group-hover/routines:opacity-100',
              sectionOpen && '-rotate-180'
            )}
          />
        </CollapsibleTrigger>
      )}
      <CollapsibleContent className="overflow-hidden will-change-[height,opacity,transform] motion-safe:data-[state=closed]:animate-collapsible-up motion-safe:data-[state=open]:animate-collapsible-down">
        {content}
      </CollapsibleContent>
    </Collapsible>
  );
}
